import type { ReactNode } from 'react'
import { AlertTriangle, Info, CheckCircle2, Lightbulb } from 'lucide-react'
import { cn } from '@/lib/utils'

type AlertVariant = 'warning' | 'danger' | 'info' | 'success' | 'tip'

const variantStyles: Record<AlertVariant, { bg: string; border: string; text: string; icon: typeof Info }> = {
  warning: { bg: 'bg-amber-50/70', border: 'border-amber-500', text: 'text-amber-800', icon: AlertTriangle },
  danger: { bg: 'bg-rose-50/70', border: 'border-rose-500', text: 'text-rose-700', icon: AlertTriangle },
  info: { bg: 'bg-blue-50/70', border: 'border-blue-500', text: 'text-blue-800', icon: Info },
  success: { bg: 'bg-emerald-50/70', border: 'border-emerald-500', text: 'text-emerald-800', icon: CheckCircle2 },
  tip: { bg: 'bg-violet-50/70', border: 'border-violet-500', text: 'text-violet-800', icon: Lightbulb },
}

interface AlertProps {
  variant?: AlertVariant
  title?: ReactNode
  children: ReactNode
  action?: ReactNode
  className?: string
}

export function Alert({ variant = 'info', title, children, action, className }: AlertProps) {
  const style = variantStyles[variant]
  const Icon = style.icon
  return (
    <div
      className={cn(
        'border-l-4 rounded-r-lg p-3 flex items-start justify-between gap-3',
        style.bg,
        style.border,
        className
      )}
    >
      <div className="flex items-start gap-2 text-sm text-slate-700">
        <Icon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', style.text)} />
        <div>
          {title && <strong className={cn('font-semibold', style.text)}>{title}</strong>}
          {title && ' '}
          {children}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  )
}
